import React from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import TopBanner from '../components/TopBanner';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, ShieldCheck, Layers, ShoppingBag } from 'lucide-react';

export default function CaseStudies() {
    const homeimg = '/images/home/home.jpg';
    const server_url = process.env.REACT_APP_SERVER;

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Lurantech Case Studies",
        "url": `${server_url}/case-studies`,
        "description": "Explore Lurantech's case studies showcasing enterprise solutions, web applications, cloud platforms, and innovative digital products delivered for global clients.",
        "publisher": {
            "@type": "Organization",
            "name": "Lurantech",
            "url": `${server_url}`,
            "logo": {
            "@type": "ImageObject",
            "url": `${server_url}/images/logo.png`
            }
        },
        "about": {
            "@type": "Organization",
            "name": "Lurantech",
            "foundingDate": "2020-01-01",
            "address": {
            "@type": "PostalAddress",
            "addressLocality": "Vadodara",
            "addressRegion": "Gujarat",
            "addressCountry": "IN"
            }
        }
    };

    const portfolios = [
        {
        id: 'women-in-ai',
        client: 'Women in AI',
        title: 'Elevating Global Analytics & Automation',
        category: 'Data Engineering',
        desc: 'Engineered an automated data pipeline to unify engagement insights across 40+ global chapters.',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
        link: '/case-studies/women-in-ai',
        icon: <BarChart3 size={18} className="me-2" />
        },
        // {
        // id: 'asean-judiciary-portal',
        // client: 'CACJ',
        // title: 'Regional Legal Integration Portal',
        // category: 'Public Sector Tech',
        // desc: 'Developed a centralized digital gateway unifying the judicial systems of 10 ASEAN member states.',
        // image: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&w=800&q=80',
        // link: '/case-studies/asean-judiciary-portal',
        // icon: <Landmark size={18} className="me-2" />
        // },
        {
        id: 'banca-aigis',
        client: 'European Digital Bank',
        title: 'Digital Banking & Compliance Workflow',
        category: 'FinTech',
        desc: 'End-to-end digital finance ecosystem integrating secure KYC and complex regulatory workflows.',
        image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80',
        link: '/case-studies/banca-aigis',
        icon: <ShieldCheck size={18} className="me-2" />
        },
        {
        id: 'european-gcc-migration',
        client: 'Leading European GCC',
        title: 'Enterprise Content Centralization',
        category: 'Cloud Migration',
        desc: 'Architected a unified Global Library, streamlining thousands of assets across a vast digital ecosystem.',
        image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&q=80',
        link: '/case-studies/european-gcc-migration',
        icon: <Layers size={18} className="me-2" />
        },
        {
        id: 'zari-gatha',
        client: 'Zari Gatha',
        title: 'Luxury E-commerce & Custom Architecture',
        category: 'E-Commerce',
        desc: 'A high-performance, visually rich Shopify storefront bridging traditional artistry with cutting-edge tech.',
        image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80',
        link: '/case-studies/zari-gatha',
        icon: <ShoppingBag size={18} className="me-2" />
        }
    ];

    const staggerContainer = {
        initial: {},
        whileInView: { transition: { staggerChildren: 0.15 } }
    };
    const fadeInUp = {
        initial: { opacity: 0, y: 40 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6, ease: "easeOut" }
    };
    return (
        <>
            <HelmetProvider>

                <Helmet>
                    <title>Lurantech | Case Studies</title>
                    <meta name="description" content="Discover Lurantech's case studies featuring enterprise-grade solutions, web platforms, cloud applications, and digital transformation projects delivered worldwide."/>
                    <meta name="keywords" content="Lurantech case studies, IT company portfolio, enterprise software projects, web development portfolio, cloud solutions, liferay projects, react projects, aws solutions"/>
                    <meta name="author" content="Lurantech" />
                    <meta name="robots" content="index, follow" />

                    <link rel="canonical" href="https://lurantech.com/portfolio" />

                    <meta property="og:title" content="Lurantech | Case Studies" />
                    <meta property="og:description" content="Explore real-world projects delivered by Lurantech across industries using cutting-edge technologies." />
                    <meta property="og:image" content={`${server_url}${homeimg}`} />
                    <meta property="og:url" content="https://lurantech.com/case-studies" />
                    <meta property="og:type" content="website" />
                    <meta property="og:image:type" content="image/jpeg" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="675" />

                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Lurantech | Case Studies" />
                    <meta name="twitter:description" content="Browse Lurantech's case studies of enterprise solutions, web apps, and cloud-based products." />
                    <meta name="twitter:image" content={`${server_url}${homeimg}`} />

                    <link rel="preload" href={`${server_url}${homeimg}`} as="image" />
                </Helmet>

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(schemaData),
                    }}
                />
                <motion.div 
                        className="text-center mb-5 pb-3"
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        >
                    <TopBanner title="Case Studies"  bgimage='/images/portfolios/portfolios_banner.png'/>
                </motion.div>        
                
                <section className="bg-light" style={{ minHeight: '100vh' }}>
                    <div className="container">
                        
                        {/* --- HEADER --- */}
                        <motion.div 
                        className="text-center mb-5 pb-3"
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        >
                        <h6 className="text-darkpurple fw-bold text-uppercase mb-2" style={{ letterSpacing: '2px' }}>
                            Our Success Stories
                        </h6>
                        <h2 className="display-4 fw-bold mb-3 text-dark">LuranTech Case Studies</h2>
                        <div className="mx-auto bg-darkpurple mb-4" style={{ height: '4px', width: '60px', borderRadius: '2px', background: 'linear-gradient(279.39deg, #5060E5 -11.76%, #7F60A6 35.07%, #352482 80.72%)' }}></div>
                        <p className="lead text-secondary mx-auto" style={{ maxWidth: '700px' }}>
                            Discover how we drive digital transformation across FinTech, E-commerce, Public Sector, and Enterprise Data.
                        </p>
                        </motion.div>

                        {/* --- GRID --- */}
                        <motion.div 
                        className="row g-4"
                        variants={staggerContainer}
                        initial="initial"
                        whileInView="whileInView"
                        viewport={{ once: true }}
                        >
                        {portfolios.map((study, index) => (
                            <motion.div key={study.id} className="col-md-6 col-lg-4" variants={fadeInUp}>
                            <div className="card h-100 border-0 shadow-sm custom-card-hover rounded-4 overflow-hidden bg-white">
                                
                                {/* Image Container */}
                                <div className="position-relative overflow-hidden" style={{ height: '240px' }}>
                                <img 
                                    src={study.image} 
                                    alt={study.title} 
                                    className="w-100 h-100 object-fit-cover card-img-custom"
                                />
                                <div className="position-absolute top-0 start-0 m-3">
                                    <span className="badge bg-white text-darkpurple px-3 py-2 shadow-sm d-flex align-items-center rounded-pill">
                                    {study.icon}<p>{study.category}</p>
                                    </span>
                                </div>
                                </div>

                                {/* Card Body */}
                                <div className="card-body p-4 d-flex flex-column">
                                <p className="text-muted small fw-bold text-uppercase mb-2 letter-spacing-1">
                                    {study.client}
                                </p>
                                <h4 className="card-title fw-bold mb-3">{study.title}</h4>
                                <p className="card-text text-secondary mb-4 flex-grow-1">
                                    {study.desc}
                                </p>
                                
                                {/* React Router Link */}
                                <Link 
                                    to={study.link} 
                                    className="btn btn-outline-primary mt-auto text-start d-flex justify-content-between align-items-center custom-btn-hover"
                                    style={{ borderRadius: '10px', borderColor: '#352482', color: '#352482' }}
                                >
                                    <span className="fw-bold">Read Case Study</span>
                                    <ArrowRight size={20} className="btn-arrow" />
                                </Link>
                                </div>
                                
                            </div>
                            </motion.div>
                        ))}
                        </motion.div>
                    </div>

                    {/* --- INLINE STYLES --- */}
                    <style dangerouslySetInnerHTML={{ __html: `
                        .text-darkpurple { color: #352482; }
                        .letter-spacing-1 { letter-spacing: 1px; }
                        
                        .custom-card-hover {
                        transition: all 0.4s ease;
                        }
                        .custom-card-hover:hover {
                        transform: translateY(-10px);
                        box-shadow: 0 20px 40px rgba(53, 36, 130, 0.15) !important;
                        }

                        .card-img-custom {
                        transition: transform 0.6s ease;
                        }
                        .custom-card-hover:hover .card-img-custom {
                        transform: scale(1.08);
                        }

                        .custom-btn-hover {
                        transition: all 0.3s ease;
                        }
                        .custom-btn-hover:hover {
                        background: linear-gradient(279.39deg, #5060E5 -11.76%, #7F60A6 35.07%, #352482 80.72%);
                        color: white !important;
                        border-color: transparent !important;
                        }
                        
                        .btn-arrow {
                        transition: transform 0.3s ease;
                        }
                        .custom-btn-hover:hover .btn-arrow {
                        transform: translateX(5px);
                        }
                    `}} />
                    </section>
            </HelmetProvider>
        </>
    );
};